import { Link } from "react-router-dom";
import { useEffect } from "react";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MessageForm from "@/pages/about/components/MessageForm";
import { usePageMeta } from "@/hooks/usePageMeta";

const services = [
  {
    icon: "ri-film-line",
    title: "影视资料整理",
    description:
      "为每部作品整理标题、封面、类型标签、主创与剧情梗概，让你在点开之前就知道它讲的是什么。",
    tone: "primary",
  },
  {
    icon: "ri-equalizer-line",
    title: "多维分类筛选",
    description:
      "类型、地区、年份支持多选组合，配合最新、热度、评分三种排序，快速缩小到几部候选。",
    tone: "accent",
  },
  {
    icon: "ri-magic-line",
    title: "标签关联推荐",
    description:
      "详情页根据类型与标签做简单匹配，推荐气质相近的作品，省去反复搜索的麻烦。",
    tone: "secondary",
  },
  {
    icon: "ri-compass-3-line",
    title: "观看指引说明",
    description:
      "在讲解作品的同时说明当前的演示状态，避免对播放能力产生误解，信息透明优先。",
    tone: "primary",
  },
];

const toneMap: Record<string, { card: string; icon: string }> = {
  primary: { card: "border-primary-100 bg-primary-50/60", icon: "bg-primary-100 text-primary-700" },
  accent: { card: "border-accent-100 bg-accent-50/60", icon: "bg-accent-100 text-accent-700" },
  secondary: {
    card: "border-secondary-200 bg-secondary-50/60",
    icon: "bg-secondary-100 text-secondary-800",
  },
};

export default function AboutPage() {
  usePageMeta({
    title: "关于我们 - 蜜桃33影视资源",
    description:
      "了解蜜桃33的定位与服务：整理电影、电视剧与综艺的剧情介绍、主创信息与相关推荐，帮助影视爱好者更高效地选片。",
    keywords: "蜜桃33,关于我们,影视资源平台,影视介绍,影视推荐",
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="flex min-h-screen w-full flex-col bg-background-50">
      <SiteHeader />

      <main className="w-full flex-1">
        <section className="w-full border-b border-background-200 bg-background-100 px-4 py-10 md:px-8 md:py-14">
          <div className="grid items-center gap-8 lg:grid-cols-[1.1fr_1fr] lg:gap-14">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-primary-600">About us</p>
              <h1 className="mt-3 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-[40px]">
                关于蜜桃33
              </h1>
              <p className="mt-4 max-w-xl text-sm leading-[1.9] text-foreground-600">
                蜜桃33 是一个面向影视爱好者的资源展示与介绍平台。我们不做冗长的排行榜，而是把每部作品的资料整理清楚，让你用更短的时间找到今晚真正想看的那一部。
              </p>
              <div className="mt-6 flex flex-wrap gap-2">
                <Link
                  to="/category/movie"
                  className="inline-flex h-10 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  开始浏览
                  <i className="ri-arrow-right-line" />
                </Link>
                <a
                  href="mailto:hello@mitao33.demo"
                  className="inline-flex h-10 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 px-5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-mail-line" />
                  联系我们
                </a>
              </div>
            </div>

            <div className="h-56 w-full overflow-hidden rounded-lg sm:h-72 lg:h-80">
              <img
                src="https://readdy.ai/api/search-image?query=Cozy%20home%20screening%20corner%20with%20a%20projector%20beam%20on%20a%20cream%20wall%2C%20soft%20peach%20lamplight%2C%20warm%20wooden%20shelf%20with%20film%20books%20and%20plants%2C%20linen%20sofa%20with%20cushions%2C%20quiet%20evening%20cinema%20atmosphere%2C%20editorial%20interior%20photography&width=900&height=700&seq=mt-about-room&orientation=landscape"
                alt="温暖的居家观影角落"
                title="蜜桃33 - 关于我们"
                className="h-full w-full object-cover object-center"
              />
            </div>
          </div>
        </section>

        <section className="w-full px-4 py-12 md:px-8 md:py-16">
          <div className="grid gap-10 lg:grid-cols-[280px_1fr] lg:gap-16">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-primary-600">Our position</p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950">我们的定位</h2>
            </div>
            <div className="space-y-4 text-sm leading-[1.95] text-foreground-600">
              <p>
                市面上的影视信息往往散落在各处：一边是冗长的评分榜单，一边是信息不全的条目页。蜜桃33 想做的是一件更朴素的事——把一部作品的基本信息、剧情介绍和同类推荐放在同一页里，让判断变得简单。
              </p>
              <p>
                我们覆盖电影、电视剧与综艺三类内容。电影看重完整的叙事气质，剧集看重人物与时间的积累，综艺看重真实场景里的松弛感。三类内容的筛选维度各自独立，不会互相干扰。
              </p>
              <p className="rounded-md border-l-2 border-accent-400 bg-accent-50/70 px-4 py-3 text-foreground-700">
                需要说明的是：本站为演示性质的前端项目，所有片名、海报、主创姓名与评分均为虚构示例，不提供真实播放，也不包含会员、登录或支付功能。
              </p>
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 px-4 py-12 md:px-8 md:py-16">
          <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-primary-600">What we offer</p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-[30px]">
                我们提供什么
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-foreground-500">
              从浏览、筛选到详情阅读，四个环节各自有明确的目标。
            </p>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 md:gap-5 lg:grid-cols-4">
            {services.map((service) => {
              const tone = toneMap[service.tone];
              return (
                <div
                  key={service.title}
                  className={`rounded-lg border p-5 transition-transform duration-300 hover:-translate-y-1 ${tone.card}`}
                >
                  <span className={`flex h-11 w-11 items-center justify-center rounded-lg ${tone.icon}`}>
                    <i className={`${service.icon} text-xl`} />
                  </span>
                  <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                    {service.title}
                  </h3>
                  <p className="mt-2 text-xs leading-relaxed text-foreground-600">
                    {service.description}
                  </p>
                </div>
              );
            })}
          </div>
        </section>

        <section className="w-full px-4 py-12 md:px-8 md:py-16">
          <div className="grid gap-10 lg:grid-cols-[1fr_1.15fr] lg:gap-14">
            <div>
              <p className="text-[11px] uppercase tracking-[0.3em] text-primary-600">Contact</p>
              <h2 className="mt-2 font-heading text-2xl font-bold text-foreground-950 md:text-[30px]">
                联系方式
              </h2>
              <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
                无论是内容建议、资料纠错还是合作咨询，都可以通过下面的方式找到我们。邮件会直接打开你的本地邮件客户端。
              </p>

              <div className="mt-6 space-y-3">
                <a
                  href="mailto:hello@mitao33.demo"
                  className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3.5 transition-colors hover:border-primary-300"
                >
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                    <i className="ri-mail-line text-lg" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-[11px] text-foreground-400">邮箱</span>
                    <span className="block truncate text-sm text-foreground-900">
                      hello@mitao33.demo
                    </span>
                  </span>
                  <i className="ri-arrow-right-up-line ml-auto text-foreground-400" />
                </a>

                <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                    <i className="ri-time-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-[11px] text-foreground-400">回复时间</span>
                    <span className="block text-sm text-foreground-900">工作日 10:00 – 18:00</span>
                  </span>
                </div>

                <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3.5">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-100 text-secondary-800">
                    <i className="ri-map-pin-line text-lg" />
                  </span>
                  <span>
                    <span className="block text-[11px] text-foreground-400">站点性质</span>
                    <span className="block text-sm text-foreground-900">线上影视资料展示 · 演示项目</span>
                  </span>
                </div>
              </div>

              <div className="mt-6 rounded-lg border border-background-200 bg-background-100 p-4">
                <h3 className="flex items-center gap-1.5 text-xs font-medium text-foreground-800">
                  <i className="ri-copyright-line text-primary-600" />
                  版权声明
                </h3>
                <p className="mt-2 text-[11px] leading-relaxed text-foreground-500">
                  站内所有影视名称、剧情文本、主创姓名与海报图像均为虚构或演示用素材，不对应任何真实作品与个人。页面结构与交互逻辑仅用于产品演示，不构成任何观看或下载指引。
                </p>
              </div>
            </div>

            <MessageForm />
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}